import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ManifestAsset, ManifestVariant } from "../types.js";
import {
  runDownload,
  dedupeFilenames,
  safeBaseName,
  extForFormat,
  type DownloadItem,
  type DownloadProgress,
  type DownloadResult,
} from "../download.js";

interface Props {
  assets: ManifestAsset[];
  onClose: () => void;
}

/**
 * One row of the format/size matrix the marketing team requested:
 *   JPEG: Original Files / 400px / 1200px
 *   WEBP: 400px / 1200px
 * `match` finds the right variant on a given asset (or undefined if it lacks one).
 */
interface DownloadOption {
  id: string;
  group: "JPEG" | "WEBP";
  label: string;
  /** Filename suffix before the extension ("" for originals, "-400", "-1200"). */
  suffix: string;
  match: (asset: ManifestAsset) => ManifestVariant | undefined;
}

const OPTIONS: DownloadOption[] = [
  {
    id: "orig",
    group: "JPEG",
    label: "Original Files",
    suffix: "",
    // "Original" = the full-resolution passthrough (width 0), whatever its source
    // format is (jpeg for ~99%, png for alpha assets, mp4/pdf for video/docs).
    match: (a) => a.variants.find((v) => v.width === 0),
  },
  { id: "jpeg-400", group: "JPEG", label: "400px", suffix: "-400", match: byWidthFormat(400, "jpeg") },
  { id: "jpeg-1200", group: "JPEG", label: "1200px", suffix: "-1200", match: byWidthFormat(1200, "jpeg") },
  { id: "webp-400", group: "WEBP", label: "400px", suffix: "-400", match: byWidthFormat(400, "webp") },
  { id: "webp-1200", group: "WEBP", label: "1200px", suffix: "-1200", match: byWidthFormat(1200, "webp") },
];

function byWidthFormat(width: number, format: string) {
  return (a: ManifestAsset) => a.variants.find((v) => v.width === width && v.format === format);
}

/** Open on a sensible, *available* option: 1200px JPEG if present, else the first
 *  option any selected asset actually has (so the modal never opens on a disabled row). */
function pickDefaultOption(assets: ManifestAsset[]): string {
  const preferred = "jpeg-1200";
  const prefOpt = OPTIONS.find((o) => o.id === preferred);
  if (prefOpt && assets.some((a) => prefOpt.match(a))) return preferred;
  for (const opt of OPTIONS) {
    if (assets.some((a) => opt.match(a))) return opt.id;
  }
  return preferred;
}

type Phase = "choose" | "running" | "done";

// Bulk downloads buffer all bytes in the browser before zipping. Warn (don't
// block) once a batch gets big enough to be slow or risky on low-memory devices.
const LARGE_BYTES = 1024 * 1024 * 1024; // ~1 GiB
const LARGE_COUNT = 300;

export function BulkDownloadModal({ assets, onClose }: Props) {
  const [optionId, setOptionId] = useState<string>(() => pickDefaultOption(assets));
  const [phase, setPhase] = useState<Phase>("choose");
  const [progress, setProgress] = useState<DownloadProgress>({ done: 0, total: 0 });
  const [result, setResult] = useState<DownloadResult | null>(null);

  const busy = phase === "running";
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  // Cancel an in-flight download (if any) and close. Used by ✕, Cancel, Escape.
  function cancelAndClose() {
    abortRef.current?.abort();
    onClose();
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelAndClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // cancelAndClose closes over stable refs/props; re-binding each render is fine.
  }, [onClose]);

  // On unmount, abort any in-flight fetches and stop further state updates.
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // Availability + total size per option, computed across the current selection.
  const stats = useMemo(() => {
    const m = new Map<string, { count: number; bytes: number }>();
    for (const opt of OPTIONS) {
      let count = 0;
      let bytes = 0;
      for (const a of assets) {
        const v = opt.match(a);
        if (v) {
          count += 1;
          bytes += v.bytes ?? 0;
        }
      }
      m.set(opt.id, { count, bytes });
    }
    return m;
  }, [assets]);

  const selectedOption = OPTIONS.find((o) => o.id === optionId);
  const selectedStat = selectedOption ? stats.get(selectedOption.id) : undefined;
  const matchCount = selectedStat?.count ?? 0;

  // Build the concrete file list for the chosen option, with deduped names.
  const items: DownloadItem[] = useMemo(() => {
    if (!selectedOption) return [];
    const entries = assets
      .map((asset) => {
        const variant = selectedOption.match(asset);
        if (!variant) return null;
        return {
          asset,
          variant,
          base: `${safeBaseName(asset.name)}${selectedOption.suffix}`,
          ext: extForFormat(variant.format),
        };
      })
      .filter((e): e is NonNullable<typeof e> => e != null);
    return dedupeFilenames(entries).map(({ asset, variant, filename }) => ({ asset, variant, filename }));
  }, [assets, selectedOption]);

  const skipped = assets.length - items.length;

  async function start() {
    if (items.length === 0 || !selectedOption) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase("running");
    setProgress({ done: 0, total: items.length });
    const zipName = `moldex-assets-${selectedOption.id}.zip`;
    let res: DownloadResult;
    try {
      res = await runDownload(
        items,
        zipName,
        (p) => mountedRef.current && setProgress(p),
        controller.signal,
      );
    } catch (e) {
      res = { delivered: 0, failed: [{ filename: "(zip)", error: e instanceof Error ? e.message : String(e) }] };
    }
    // If we were unmounted (closed/cancelled), don't touch state.
    if (!mountedRef.current || res.aborted) return;
    setResult(res);
    setPhase("done");
  }

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const large =
    !busy && matchCount > 0 && selectedStat != null && (selectedStat.bytes > LARGE_BYTES || matchCount > LARGE_COUNT);

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        className="modal bulk-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-busy={busy}
      >
        <header>
          <h2>Download {assets.length} {assets.length === 1 ? "item" : "items"}</h2>
          <button
            className="close"
            aria-label={busy ? "Cancel download and close" : "Close"}
            onClick={cancelAndClose}
          >
            ×
          </button>
        </header>

        <div className="modal-body">
          {phase === "done" && result ? (
            <DoneSummary result={result} skipped={skipped} optionLabel={labelFor(selectedOption)} />
          ) : (
            <>
              <p className="bulk-intro">Choose a format and size. Everything you picked downloads in that one format.</p>
              <fieldset className="bulk-options" disabled={busy}>
                {(["JPEG", "WEBP"] as const).map((group) => (
                  <div key={group} className="dl-group">
                    <h4>{group}</h4>
                    {OPTIONS.filter((o) => o.group === group).map((opt) => {
                      const st = stats.get(opt.id)!;
                      const unavailable = st.count === 0;
                      return (
                        <label
                          key={opt.id}
                          className={`dl-option ${unavailable ? "dl-option-disabled" : ""}`}
                          title={
                            unavailable
                              ? `${group} ${opt.label} — none of the selected items have this version`
                              : undefined
                          }
                        >
                          <input
                            type="radio"
                            name="bulk-format"
                            value={opt.id}
                            checked={optionId === opt.id}
                            disabled={unavailable}
                            onChange={() => setOptionId(opt.id)}
                            aria-label={
                              unavailable
                                ? `${group} ${opt.label}, not available for this selection`
                                : `${group} ${opt.label}, ${st.count} of ${assets.length} items`
                            }
                          />
                          <span className="dl-option-label">{opt.label}</span>
                          <span className="dl-option-meta">
                            {unavailable
                              ? "none available"
                              : `${st.count} of ${assets.length}${st.bytes ? ` · ${formatBytes(st.bytes)}` : ""}`}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </fieldset>

              {busy ? (
                <div className="bulk-progress" aria-live="polite">
                  <div
                    className="bulk-progress-bar"
                    role="progressbar"
                    aria-label="Download progress"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div className="bulk-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="bulk-progress-text">
                    Fetching {progress.done} of {progress.total}…
                  </div>
                </div>
              ) : (
                <>
                  {skipped > 0 && matchCount > 0 && (
                    <p className="bulk-skip-note">
                      {skipped} of {assets.length} selected {skipped === 1 ? "item has" : "items have"} no{" "}
                      {labelFor(selectedOption)} version and will be skipped.
                    </p>
                  )}
                  {large && selectedStat && (
                    <p className="bulk-large-note">
                      Large download (~{formatBytes(selectedStat.bytes)}, {matchCount.toLocaleString()} files). It is
                      built in your browser, so it may take a while and could fail on a low-memory device.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <footer className="bulk-footer">
          {phase === "done" ? (
            <button className="bulk-btn bulk-btn-primary" onClick={onClose}>
              Done
            </button>
          ) : (
            <>
              <button className="bulk-btn" onClick={cancelAndClose}>
                Cancel
              </button>
              <button
                className="bulk-btn bulk-btn-primary"
                onClick={() => void start()}
                disabled={busy || matchCount === 0}
                title={matchCount === 0 ? "No selected items have this version" : undefined}
              >
                {busy
                  ? "Preparing…"
                  : matchCount > 1
                    ? `Download ${matchCount} as .zip`
                    : matchCount === 1
                      ? "Download 1 file"
                      : "Nothing to download"}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

function DoneSummary({
  result,
  skipped,
  optionLabel,
}: {
  result: DownloadResult;
  skipped: number;
  optionLabel: string;
}) {
  return (
    <div className="bulk-done">
      <p className="bulk-done-headline">
        {result.delivered > 0
          ? `Downloaded ${result.delivered} ${result.delivered === 1 ? "file" : "files"}.`
          : "Nothing was downloaded."}
      </p>
      {skipped > 0 && (
        <p className="bulk-done-note">
          {skipped} {skipped === 1 ? "item" : "items"} had no {optionLabel} version and {skipped === 1 ? "was" : "were"} skipped.
        </p>
      )}
      {result.failed.length > 0 && (
        <div className="bulk-done-errors">
          <p>{result.failed.length} {result.failed.length === 1 ? "file" : "files"} failed to fetch:</p>
          <ul>
            {result.failed.slice(0, 8).map((f) => (
              <li key={f.filename}>
                {f.filename} <span className="bulk-err">({f.error})</span>
              </li>
            ))}
            {result.failed.length > 8 && <li>+{result.failed.length - 8} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function labelFor(opt: DownloadOption | undefined): string {
  if (!opt) return "";
  return opt.group === "JPEG" && opt.id === "orig" ? "original" : `${opt.group} ${opt.label}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
